import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
    Table,
    Tag,
    Card,
    Space,
    Statistic,
    Row,
    Col,
    Select,
    Button,
    Modal,
    Form,
    Input,
    InputNumber,
    DatePicker,
    message,
    Tooltip,
    Badge,
    Typography,
    Rate,
    theme,
    Grid,
    List,
    Alert,
    Checkbox,
    Empty,
    Spin,
} from 'antd';
import {
    PhoneOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
    ClockCircleOutlined,
    StarOutlined,
    SyncOutlined,
    SearchOutlined,
    CommentOutlined,
    UserAddOutlined,
    HistoryOutlined,
    StopOutlined,
    InboxOutlined,
    MoreOutlined,
    BellOutlined,
    UserOutlined,
    WarningOutlined,
} from '@ant-design/icons';
import { crmApi, ordersApi } from '../../api/client';
import RichMentionEditor from '../../components/common/RichMentionEditor';
import MentionDisplay from '../../components/common/MentionDisplay';
import { useNotificationRefresh } from '../../hooks/useNotificationRefresh';
import { useAuthStore } from '../../store/auth.store';
import dayjs from 'dayjs';

const { TextArea } = Input;
const { Text } = Typography;
const { useBreakpoint } = Grid;

const contactStatusConfig: Record<string, { label: string; color: string }> = {
    PENDING: { label: 'Do kontaktu', color: 'default' },
    CONTACTED: { label: 'Skontaktowany', color: 'blue' },
    NO_ANSWER: { label: 'Brak odpowiedzi', color: 'orange' },
    REVIEW_REQUESTED: { label: 'Poproszono o opinię', color: 'green' },
    DECLINED: { label: 'Odmówił', color: 'red' },
    CALLBACK: { label: 'Oddzwonić', color: 'purple' },
    EXCLUDED: { label: 'Wykluczony', color: 'default' },
};

const reviewStatusConfig: Record<string, { label: string; color: string }> = {
    NONE: { label: '-', color: 'default' },
    PENDING: { label: 'Oczekuje', color: 'processing' },
    RECEIVED: { label: 'Otrzymano', color: 'success' },
    NOT_RECEIVED: { label: 'Nie zostawił', color: 'error' },
};

import { useNavigate, useLocation } from 'react-router-dom';

// Readable product name: catalogue name if the line is linked to a product, otherwise the offer title, SKU as last resort
const productName = (line: any): string => line.product?.name || line.name || line.sku || '-';

const productsSummary = (lines: any[] = []) =>
    lines.map((l: any) => `${l.quantity > 1 ? `${l.quantity}× ` : ''}${productName(l)}`);

// Earlier purchases of the same customer, computed by the backend (matched by e-mail or phone)
function CustomerBadges({ history }: { history?: any }) {
    if (!history) return null;
    const prev = history.previousOrdersCount || 0;
    const tags: React.ReactNode[] = [];

    if (prev > 0) {
        const isLoyal = prev >= 2;
        tags.push(
            <Tooltip
                key="returning"
                title={
                    <div>
                        <div style={{ marginBottom: 4 }}>
                            {prev} wcześniejsze zamówienia · {Number(history.previousOrdersTotal || 0).toFixed(2)} zł
                            {history.firstOrderDate && <> · klient od {dayjs(history.firstOrderDate).format('MM.YYYY')}</>}
                        </div>
                        {history.previousOrders?.map((o: any) => (
                            <div key={o.id} style={{ fontSize: 12 }}>
                                {dayjs(o.orderDate).format('DD.MM.YYYY')}: {o.products?.join(', ') || '-'}
                            </div>
                        ))}
                    </div>
                }
            >
                <Tag color={isLoyal ? 'gold' : 'cyan'} style={{ marginInlineEnd: 4 }}>
                    <UserAddOutlined /> {isLoyal ? 'Stały klient' : 'Powracający'} · {prev + 1}. zakup
                </Tag>
            </Tooltip>
        );
    }
    if (history.recentlyContacted && history.lastTalk) {
        tags.push(
            <Tooltip key="talked" title={lastTalkText(history.lastTalk)}>
                <Tag color="orange" icon={<WarningOutlined />} style={{ marginInlineEnd: 4 }}>
                    Rozmowa {dayjs(history.lastTalk.at).format('DD.MM')}
                </Tag>
            </Tooltip>
        );
    }
    if (history.laterOrdersCount > 0) {
        tags.push(
            <Tooltip key="later" title="Klient złożył kolejne zamówienie po tym zakupie – jest zadowolony, dobry moment na prośbę o opinię">
                <Tag color="green" style={{ marginInlineEnd: 4 }}>Kupił ponownie</Tag>
            </Tooltip>
        );
    }
    if (history.reviewAlreadyGiven) {
        tags.push(
            <Tooltip key="review" title="Przy wcześniejszym zakupie klient już wystawił opinię – podziękuj zamiast prosić ponownie">
                <Tag color="success" style={{ marginInlineEnd: 4 }}><StarOutlined /> Wystawił opinię</Tag>
            </Tooltip>
        );
    }
    if (history.declinedBefore) {
        tags.push(
            <Tooltip key="declined" title="Przy wcześniejszym kontakcie klient odmówił">
                <Tag color="red" style={{ marginInlineEnd: 4 }}>Wcześniej odmówił</Tag>
            </Tooltip>
        );
    }
    return tags.length ? <div style={{ marginTop: 4 }}>{tags}</div> : null;
}

const lastTalkText = (talk: any) =>
    `${dayjs(talk.at).format('DD.MM.YYYY HH:mm')}: ${contactStatusConfig[talk.status]?.label || talk.status}`
    + `${talk.byName ? ` (${talk.byName})` : ''}${talk.orderNumber ? `, zamówienie ${talk.orderNumber}` : ''}`;

const userName = (u?: any) => (u ? `${u.firstName || ''} ${u.lastName || ''}`.trim() : '');

function AssigneeTag({ user, currentUserId }: { user?: any; currentUserId?: string }) {
    if (!user) return null;
    const isMe = user.id === currentUserId;
    return (
        <Tooltip title={isMe ? 'Klient przypisany do Ciebie' : `Klientem zajmuje się ${userName(user)} – nie dzwoń bez uzgodnienia`}>
            <Tag color={isMe ? 'blue' : 'geekblue'} icon={<UserOutlined />} style={{ marginInlineEnd: 4 }}>
                {isMe ? 'Mój klient' : userName(user)}
            </Tag>
        </Tooltip>
    );
}

// All orders of the customer with every contact attempt, so nobody calls twice about the same thing
function CustomerTimeline({ contactId }: { contactId: string }) {
    const { token } = theme.useToken();
    const { data, isLoading } = useQuery({
        queryKey: ['crm-timeline', contactId],
        queryFn: () => crmApi.getCustomerTimeline(contactId).then((res) => res.data),
    });

    if (isLoading) return <div style={{ textAlign: 'center', padding: 16 }}><Spin /></div>;
    if (!data?.entries?.length) return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Brak historii" />;

    return (
        <div>
            {data.entries.map((entry: any, i: number) => (
                <div
                    key={entry.orderId || entry.contacts[0]?.id || i}
                    style={{
                        borderLeft: `3px solid ${entry.isCurrent ? token.colorPrimary : token.colorBorder}`,
                        padding: '4px 0 4px 10px',
                        marginBottom: 10,
                    }}
                >
                    <div style={{ fontSize: 13 }}>
                        <strong>{entry.orderDate ? dayjs(entry.orderDate).format('DD.MM.YYYY') : '-'}</strong>
                        {entry.orderNumber && <> · {entry.orderNumber}</>}
                        {' · '}{Number(entry.total || 0).toFixed(2)} zł
                        {entry.channelName && <Text type="secondary"> · {entry.channelName}</Text>}
                        {entry.isCurrent && <Tag color="blue" style={{ marginInlineStart: 6 }}>to zamówienie</Tag>}
                    </div>
                    {entry.products?.length > 0 && (
                        <div style={{ fontSize: 12, color: token.colorTextSecondary }}>{entry.products.join(', ')}</div>
                    )}
                    {entry.contacts.length === 0 && (
                        <Text type="secondary" style={{ fontSize: 12 }}>Bez kontaktu w sprawie opinii</Text>
                    )}
                    {entry.contacts.map((c: any) => (
                        <div key={c.id} style={{ marginTop: 4 }}>
                            <Space size={4} wrap>
                                <Tag color={contactStatusConfig[c.contactStatus]?.color} style={{ marginInlineEnd: 0 }}>
                                    {contactStatusConfig[c.contactStatus]?.label || c.contactStatus}
                                </Tag>
                                {c.reviewStatus !== 'NONE' && (
                                    <Badge status={reviewStatusConfig[c.reviewStatus]?.color as any} text={<span style={{ fontSize: 12 }}>Opinia: {reviewStatusConfig[c.reviewStatus]?.label}</span>} />
                                )}
                                {c.nextContactDate && <ReminderTag date={c.nextContactDate} />}
                            </Space>
                            {c.events.length === 0 && (
                                <div style={{ fontSize: 12 }}><Text type="secondary">Jeszcze nikt nie dzwonił</Text></div>
                            )}
                            {c.events.map((e: any, j: number) => (
                                <div key={j} style={{ fontSize: 12, marginTop: 2 }}>
                                    <Text type="secondary">{dayjs(e.at).format('DD.MM.YYYY HH:mm')}</Text>
                                    {' · '}{contactStatusConfig[e.status]?.label || e.status}
                                    {e.byName && <> · <strong>{e.byName}</strong></>}
                                    {e.notes && <div style={{ color: token.colorTextSecondary, whiteSpace: 'pre-wrap' }}><MentionDisplay content={e.notes} /></div>}
                                </div>
                            ))}
                        </div>
                    ))}
                </div>
            ))}
        </div>
    );
}

// Talking points for the contact modal, based on the customer's history
function customerHint(history?: any): string | null {
    if (!history) return null;
    if (history.recentlyContacted) return 'Z klientem już niedawno rozmawiano przy innym zamówieniu – nie dzwoń ponownie w sprawie opinii. Najlepiej zapisz wynik tamtej rozmowy także dla tego zamówienia.';
    if (history.reviewAlreadyGiven) return 'Klient już wcześniej wystawił opinię. Podziękuj za ponowny zakup i zapytaj o zadowolenie – nie proś drugi raz o opinię.';
    if (history.declinedBefore) return 'Klient wcześniej odmówił. Skup się na zadowoleniu z produktu, prośbę o opinię zostaw tylko jeśli rozmowa pójdzie dobrze.';
    if (history.previousOrdersCount >= 2) return 'Stały klient – podziękuj za zaufanie. Możesz zapytać, jak sprawdzają się wcześniej kupione produkty.';
    if (history.previousOrdersCount === 1) return 'Klient wrócił po raz drugi – podziękuj za powrót i zapytaj, co go przekonało.';
    if (history.laterOrdersCount > 0) return 'Klient kupił ponownie po tym zamówieniu – jest zadowolony, to dobry moment na prośbę o opinię.';
    return null;
}

const quickTagStyle = (checked: boolean): React.CSSProperties => ({
    border: '1px solid',
    borderColor: checked ? 'transparent' : 'rgba(128,128,128,0.35)',
    padding: '2px 10px',
    fontSize: 13,
    marginInlineEnd: 0,
});

function ReminderTag({ date }: { date?: string }) {
    if (!date) return null;
    const isDue = dayjs(date).isBefore(dayjs());
    return (
        <Tooltip title={isDue ? 'Termin przypomnienia minął' : 'Zaplanowane przypomnienie'}>
            <Tag color={isDue ? 'red' : 'purple'} icon={<BellOutlined />} style={{ marginInlineEnd: 0 }}>
                {dayjs(date).format('DD.MM HH:mm')}
            </Tag>
        </Tooltip>
    );
}

export default function CrmPage() {
    const screens = useBreakpoint();
    const isMobile = !screens.md;
    const { token } = theme.useToken();
    const navigate = useNavigate();
    const location = useLocation();

    // Check if we are in B2B mode (simple check for now, can be improved with Tabs)
    const isB2B = location.pathname.includes('/b2b');

    const [searchParams, setSearchParams] = useSearchParams();
    const [isContactModalOpen, setIsContactModalOpen] = useState(false);
    const [selectedContact, setSelectedContact] = useState<any>(null);
    const [filterContactStatus, setFilterContactStatus] = useState<string | undefined>();
    const [filterReviewStatus, setFilterReviewStatus] = useState<string | undefined>();
    const [filterChannel, setFilterChannel] = useState<string | undefined>();
    const [searchText, setSearchText] = useState<string>('');
    const [historyContactId, setHistoryContactId] = useState<string | null>(null);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [readyToContact, setReadyToContact] = useState(false);
    const [returningOnly, setReturningOnly] = useState(false);
    // Quick filters are remembered per browser, so everyone lands on their own view
    const [quick, setQuickState] = useState<{ assignedTo?: string; remindersOnly: boolean; toCall: boolean }>(() => {
        try {
            return { remindersOnly: false, toCall: false, ...JSON.parse(localStorage.getItem('crm-quick-filters') || '{}') };
        } catch {
            return { remindersOnly: false, toCall: false };
        }
    });
    const setQuick = (patch: Partial<typeof quick>) => {
        const next = { ...quick, ...patch };
        setQuickState(next);
        setPage(1);
        try { localStorage.setItem('crm-quick-filters', JSON.stringify(next)); } catch { /* storage unavailable */ }
    };
    const currentUserId = useAuthStore((state) => state.user?.id);
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
    const [sortField, setSortField] = useState<string | undefined>('deliveredAt');
    const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC' | undefined>('DESC');
    const [contactForm] = Form.useForm();
    const queryClient = useQueryClient();

    const refreshNotifications = useNotificationRefresh();

    const { data: contactsData, isLoading: contactsLoading } = useQuery({
        queryKey: ['crm-contacts', filterContactStatus, filterReviewStatus, filterChannel, searchText, page, pageSize, readyToContact, returningOnly, quick, sortField, sortOrder],
        queryFn: () => crmApi.getContacts({
            contactStatus: filterContactStatus,
            reviewStatus: filterReviewStatus,
            channelId: filterChannel,
            search: searchText || undefined,
            page,
            limit: pageSize,
            readyToContact,
            returningOnly,
            assignedTo: quick.assignedTo,
            remindersOnly: quick.remindersOnly,
            toCall: quick.toCall,
            sortField,
            sortOrder,
        }).then((res) => res.data),
    });

    const { data: assignees = [] } = useQuery({
        queryKey: ['crm-assignees'],
        queryFn: () => crmApi.getAssignees().then((res) => res.data),
        staleTime: 5 * 60 * 1000,
    });

    const assignMutation = useMutation({
        mutationFn: ({ id, userId }: { id: string; userId: string | null }) =>
            crmApi.assignCustomer(id, userId).then((res) => res.data),
        onSuccess: (data, { id, userId }) => {
            queryClient.invalidateQueries({ queryKey: ['crm-contacts'] });
            queryClient.invalidateQueries({ queryKey: ['crm-stats'] });
            queryClient.invalidateQueries({ queryKey: ['crm-timeline'] });
            const user = assignees.find((u: any) => u.id === userId);
            setSelectedContact((prev: any) => (prev?.id === id ? { ...prev, assignedToId: userId, assignedTo: user || null } : prev));
            message.success(userId
                ? `Przypisano do: ${userName(user)}${data.updated > 1 ? ` (${data.updated} zamówień klienta)` : ''}`
                : 'Usunięto przypisanie');
        },
        onError: () => message.error('Błąd przypisywania'),
    });

    const { data: statsData } = useQuery({
        queryKey: ['crm-stats'],
        queryFn: () => crmApi.getStats().then((res) => res.data),
    });

    const { data: channelsData } = useQuery({
        queryKey: ['channels'],
        queryFn: () => ordersApi.getChannels().then((res) => res.data),
    });

    const updateStatusMutation = useMutation({
        mutationFn: ({ id, data }: { id: string; data: any }) =>
            crmApi.updateContactStatus(id, data).then((res) => res.data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['crm-contacts'] });
            queryClient.invalidateQueries({ queryKey: ['crm-stats'] });
            queryClient.invalidateQueries({ queryKey: ['crm-timeline'] });
            refreshNotifications();
            message.success('Status zaktualizowany');
            closeContactModal();
            contactForm.resetFields();
        },
        onError: () => message.error('Błąd aktualizacji statusu'),
    });

    const markReviewReceivedMutation = useMutation({
        mutationFn: (id: string) => crmApi.markReviewReceived(id).then((res) => res.data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['crm-contacts'] });
            queryClient.invalidateQueries({ queryKey: ['crm-stats'] });
            message.success('Oznaczono jako otrzymaną');
        },
    });

    const markReviewNotReceivedMutation = useMutation({
        mutationFn: (id: string) => crmApi.markReviewNotReceived(id).then((res) => res.data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['crm-contacts'] });
            queryClient.invalidateQueries({ queryKey: ['crm-stats'] });
            message.success('Oznaczono jako nieotrzymaną');
        },
    });

    const generateContactsMutation = useMutation({
        mutationFn: (params: { daysBack?: number, includeAll?: boolean }) =>
            crmApi.generateContacts(params.daysBack || 60, params.includeAll).then((res) => res.data),
        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: ['crm-contacts'] });
            queryClient.invalidateQueries({ queryKey: ['crm-stats'] });
            message.success(data.message);
        },
        onError: () => message.error('Błąd generowania wpisów'),
    });

    const bulkExcludeMutation = useMutation({
        mutationFn: (ids: string[]) => crmApi.bulkExclude(ids, 'Wykluczony z kontaktu').then((res) => res.data),
        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: ['crm-contacts'] });
            queryClient.invalidateQueries({ queryKey: ['crm-stats'] });
            message.success(`Wykluczono ${data.excluded} kontaktów`);
            setSelectedRowKeys([]);
        },
        onError: () => message.error('Błąd wykluczania kontaktów'),
    });

    const handleBulkExclude = () => {
        if (selectedRowKeys.length === 0) {
            message.warning('Zaznacz kontakty do wykluczenia');
            return;
        }
        Modal.confirm({
            title: 'Wyklucz zaznaczone kontakty',
            content: `Czy na pewno chcesz wykluczyć ${selectedRowKeys.length} kontaktów? Nie będą się pojawiać na liście.`,
            okText: 'Wyklucz',
            okType: 'danger',
            cancelText: 'Anuluj',
            onOk: () => bulkExcludeMutation.mutate(selectedRowKeys as string[]),
        });
    };


    const handleOpenContactModal = (record: any) => {
        setSelectedContact(record);
        contactForm.setFieldsValue({
            notes: '',
            qualityRating: null,
            nextContactDate: null,
            applyToOpenSiblings: true,
        });
        setIsContactModalOpen(true);
    };

    const closeContactModal = () => {
        setIsContactModalOpen(false);
        setSelectedContact(null);
        if (searchParams.has('contactId')) {
            const newParams = new URLSearchParams(searchParams);
            newParams.delete('contactId');
            setSearchParams(newParams, { replace: true });
        }
    };

    const handleContactSubmit = (values: any) => {
        if (!selectedContact) return;

        updateStatusMutation.mutate({
            id: selectedContact.id,
            data: {
                status: values.status,
                notes: values.notes,
                qualityRating: values.qualityRating,
                nextContactDate: values.nextContactDate?.toISOString(),
                applyToOpenSiblings: selectedContact.customerHistory?.openElsewhereCount > 0 && values.applyToOpenSiblings,
            },
        });
    };

    // Deep linking
    useEffect(() => {
        const contactId = searchParams.get('contactId');
        if (contactId && !isContactModalOpen) {
            const contacts = contactsData?.data || contactsData || [];
            const found = contacts.find((c: any) => c.id === contactId);
            if (found) {
                // Avoid reopening if already open/selected?
                if (selectedContact?.id !== contactId) {
                    handleOpenContactModal(found);
                }
            } else {
                crmApi.getContact(contactId)
                    .then(res => {
                        if (selectedContact?.id !== contactId) {
                            handleOpenContactModal(res.data);
                        }
                    })
                    .catch(() => {
                        message.error('Nie znaleziono kontaktu');
                        const newParams = new URLSearchParams(searchParams);
                        newParams.delete('contactId');
                        setSearchParams(newParams);
                    });
            }
        }
    }, [searchParams, contactsData]);

    const columns = [
        {
            title: 'Klient',
            dataIndex: 'customerName',
            key: 'customerName',
            render: (name: string, record: any) => (
                <div>
                    <div style={{ fontWeight: 500 }}>{name || 'Brak nazwy'}</div>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        {record.customerEmail}
                    </Text>
                    <div style={{ marginTop: 4 }}>
                        <AssigneeTag user={record.assignedTo} currentUserId={currentUserId} />
                    </div>
                    <CustomerBadges history={record.customerHistory} />
                </div>
            ),
        },
        {
            title: 'Produkty',
            key: 'products',
            width: 220,
            render: (_: any, record: any) => {
                const lines = record.order?.lines || [];
                if (lines.length === 0) return <Text type="secondary">-</Text>;
                return (
                    <Tooltip title={<div>{lines.map((l: any, i: number) => <div key={i}>{l.quantity}× {l.name || productName(l)}{l.sku ? ` (${l.sku})` : ''}</div>)}</div>}>
                        <div style={{ fontSize: 12, maxHeight: 60, overflow: 'auto', maxWidth: 220 }}>
                            {lines.map((l: any, i: number) => (
                                <div key={i} style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {l.quantity > 1 && <span style={{ color: token.colorPrimary }}>{l.quantity}× </span>}
                                    {productName(l)}
                                </div>
                            ))}
                        </div>
                    </Tooltip>
                );
            },
        },
        {
            title: 'Telefon',
            dataIndex: 'customerPhone',
            key: 'customerPhone',
            render: (phone: string) => phone ? (
                <a href={`tel:${phone}`}><PhoneOutlined /> {phone}</a>
            ) : '-',
        },
        {
            title: 'Data dostawy',
            dataIndex: 'deliveredAt',
            key: 'deliveredAt',
            render: (date: string) => date ? dayjs(date).format('DD.MM.YYYY') : '-',
            sorter: true,
            defaultSortOrder: 'descend' as const,
        },
        {
            title: 'Dni od zam.',
            key: 'daysSinceOrder',
            width: 90,
            render: (_: any, record: any) => {
                const orderDate = record.deliveredAt || record.createdAt;
                if (!orderDate) return '-';
                const days = dayjs().diff(dayjs(orderDate), 'day');
                const color = days >= 30 ? '#52c41a' : days >= 14 ? '#faad14' : '#ff4d4f';
                return <Tag color={color}>{days} dni</Tag>;
            },
        },
        {
            title: 'Wartość',
            dataIndex: 'orderTotal',
            key: 'orderTotal',
            render: (total: number) => total ? `${Number(total).toFixed(2)} zł` : '-',
            sorter: true,
        },
        {
            title: 'Kanał',
            dataIndex: 'channelName',
            key: 'channelName',
            render: (name: string) => name || '-',
        },
        {
            title: 'Status kontaktu',
            dataIndex: 'contactStatus',
            key: 'contactStatus',
            render: (status: string, record: any) => {
                const config = contactStatusConfig[status] || { label: status, color: 'default' };
                return (
                    <Space direction="vertical" size={4}>
                        <Tag color={config.color} style={{ marginInlineEnd: 0 }}>{config.label}</Tag>
                        <ReminderTag date={record.nextContactDate} />
                    </Space>
                );
            },
        },
        {
            title: 'Opinia',
            dataIndex: 'reviewStatus',
            key: 'reviewStatus',
            render: (status: string, record: any) => {
                const config = reviewStatusConfig[status] || { label: status, color: 'default' };
                return (
                    <Space>
                        <Badge status={config.color as any} text={config.label} />
                        {record.qualityRating && (
                            <Tooltip title="Ocena jakości">
                                <Rate disabled value={record.qualityRating} style={{ fontSize: 12 }} />
                            </Tooltip>
                        )}
                    </Space>
                );
            },
        },
        {
            title: 'Próby',
            dataIndex: 'contactAttempts',
            key: 'contactAttempts',
            render: (attempts: number) => attempts || 0,
        },
        {
            title: 'Akcje',
            key: 'actions',
            render: (_: any, record: any) => (
                <Space>
                    <Tooltip title="Zadzwoń / Zaktualizuj">
                        <Button
                            type="primary"
                            icon={<PhoneOutlined />}
                            size="small"
                            onClick={() => handleOpenContactModal(record)}
                        />
                    </Tooltip>
                    {record.reviewStatus === 'PENDING' && (
                        <>
                            <Tooltip title="Otrzymano opinię">
                                <Button
                                    type="default"
                                    icon={<CheckCircleOutlined />}
                                    size="small"
                                    style={{ color: 'green', borderColor: 'green' }}
                                    onClick={() => markReviewReceivedMutation.mutate(record.id)}
                                />
                            </Tooltip>
                            <Tooltip title="Nie zostawił opinii">
                                <Button
                                    type="default"
                                    icon={<CloseCircleOutlined />}
                                    size="small"
                                    style={{ color: 'red', borderColor: 'red' }}
                                    onClick={() => markReviewNotReceivedMutation.mutate(record.id)}
                                />
                            </Tooltip>
                        </>
                    )}
                    {record.notes && (
                        <Tooltip title={<MentionDisplay content={record.notes} />}>
                            <Button type="text" icon={<CommentOutlined />} size="small" />
                        </Tooltip>
                    )}
                    <Tooltip title="Historia klienta – wszystkie zamówienia i kontakty">
                        <Button
                            type="text"
                            icon={<HistoryOutlined />}
                            size="small"
                            onClick={() => setHistoryContactId(record.id)}
                        />
                    </Tooltip>
                </Space>
            ),
        },
    ];

    return (
        <div style={{ padding: isMobile ? '8px' : 24 }}>
            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                <Col xs={12} sm={12} md={6}>
                    <Card size="small">
                        <Statistic
                            title="Do kontaktu"
                            value={statsData?.pending || 0}
                            prefix={<ClockCircleOutlined />}
                            valueStyle={{ fontSize: isMobile ? 16 : 24 }}
                        />
                    </Card>
                </Col>
                <Col xs={12} sm={12} md={6}>
                    <Card size="small">
                        <Statistic
                            title="Skontaktowani dziś"
                            value={statsData?.contactedToday || 0}
                            prefix={<PhoneOutlined />}
                            valueStyle={{ color: '#1890ff', fontSize: isMobile ? 16 : 24 }}
                        />
                    </Card>
                </Col>
                <Col xs={12} sm={12} md={6}>
                    <Card size="small">
                        <Statistic
                            title="Oczekuje"
                            value={statsData?.awaitingReview || 0}
                            prefix={<StarOutlined />}
                            valueStyle={{ color: '#faad14', fontSize: isMobile ? 16 : 24 }}
                        />
                    </Card>
                </Col>
                <Col xs={12} sm={12} md={6}>
                    <Card size="small">
                        <Statistic
                            title="Opinie (tydz.)"
                            value={statsData?.reviewsReceivedThisWeek || 0}
                            prefix={<CheckCircleOutlined />}
                            valueStyle={{ color: '#52c41a', fontSize: isMobile ? 16 : 24 }}
                        />
                    </Card>
                </Col>
            </Row>

            <Card
                title="Opinie i kontakt (B2C)"
                extra={!isMobile && (
                    <Space>
                        <Button
                            icon={<SyncOutlined />}
                            onClick={() => generateContactsMutation.mutate({ daysBack: 60 })}
                            loading={generateContactsMutation.isPending}
                        >
                            Generuj (30-60 dni)
                        </Button>
                        <Button
                            icon={<HistoryOutlined />}
                            onClick={() => {
                                Modal.confirm({
                                    title: 'Generuj wszystkie kontakty',
                                    content: 'Czy na pewno chcesz wygenerować wpisy kontaktowe dla WSZYSTKICH historycznych zamówień? Może to zająć chwilę.',
                                    onOk: () => generateContactsMutation.mutate({ includeAll: true }),
                                });
                            }}
                            loading={generateContactsMutation.isPending}
                        >
                            Generuj wszystkie
                        </Button>
                    </Space>
                )}
            >
                {isMobile && (
                    <div style={{ marginBottom: 16 }}>
                        <Space direction="vertical" style={{ width: '100%' }}>
                            <Button
                                block
                                icon={<SyncOutlined />}
                                onClick={() => generateContactsMutation.mutate({ daysBack: 60 })}
                                loading={generateContactsMutation.isPending}
                            >
                                Generuj (30-60 dni)
                            </Button>
                            <Input
                                placeholder="Szukaj klienta..."
                                prefix={<SearchOutlined />}
                                value={searchText}
                                onChange={(e) => setSearchText(e.target.value)}
                                allowClear
                            />
                        </Space>
                    </div>
                )}
                <Space style={{ marginBottom: 16 }} wrap>
                    {!isMobile && (
                        <Input
                            placeholder="Szukaj klienta..."
                            prefix={<SearchOutlined />}
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            style={{ width: 200 }}
                            allowClear
                        />
                    )}
                    <Select
                        placeholder="Status kontaktu"
                        style={{ width: isMobile ? '100%' : 180 }}
                        allowClear
                        value={filterContactStatus}
                        onChange={setFilterContactStatus}
                        options={Object.entries(contactStatusConfig).map(([key, val]) => ({
                            value: key,
                            label: val.label,
                        }))}
                    />
                    <Select
                        placeholder="Status opinii"
                        style={{ width: isMobile ? '100%' : 150 }}
                        allowClear
                        value={filterReviewStatus}
                        onChange={setFilterReviewStatus}
                        options={Object.entries(reviewStatusConfig).map(([key, val]) => ({
                            value: key,
                            label: val.label,
                        }))}
                    />
                    {!isMobile && (
                        <Select
                            placeholder="Kanał"
                            style={{ width: 150 }}
                            allowClear
                            value={filterChannel}
                            onChange={setFilterChannel}
                            options={channelsData?.map((c: any) => ({
                                value: c.id,
                                label: c.name,
                            })) || []}
                        />
                    )}
                    {!isMobile && (
                        <Select
                            placeholder="Przypisany do"
                            style={{ width: 170 }}
                            allowClear
                            showSearch
                            optionFilterProp="label"
                            value={quick.assignedTo}
                            onChange={(value) => setQuick({ assignedTo: value })}
                            options={[
                                { value: 'me', label: 'Do mnie' },
                                { value: 'none', label: 'Nieprzypisani' },
                                ...assignees.filter((u: any) => u.id !== currentUserId).map((u: any) => ({ value: u.id, label: userName(u) })),
                            ]}
                        />
                    )}
                    {selectedRowKeys.length > 0 && (
                        <Button
                            danger
                            size="small"
                            icon={<StopOutlined />}
                            onClick={handleBulkExclude}
                            loading={bulkExcludeMutation.isPending}
                        >
                            Wyklucz ({selectedRowKeys.length})
                        </Button>
                    )}
                </Space>

                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 16 }}>
                    <Tooltip title="Gotowi do telefonu (30+ dni od dostawy), bez zaplanowanego oddzwonienia, nie przypisani do kogoś innego i bez rozmowy przy innym zamówieniu w ostatnich 90 dniach">
                        <Tag.CheckableTag checked={quick.toCall} onChange={(checked) => setQuick({ toCall: checked })} style={quickTagStyle(quick.toCall)}>
                            <PhoneOutlined /> Do dzwonienia
                        </Tag.CheckableTag>
                    </Tooltip>
                    <Tag.CheckableTag
                        checked={quick.assignedTo === 'me'}
                        onChange={(checked) => setQuick({ assignedTo: checked ? 'me' : undefined })}
                        style={quickTagStyle(quick.assignedTo === 'me')}
                    >
                        <UserOutlined /> Moi klienci{statsData?.myAssigned ? ` (${statsData.myAssigned})` : ''}
                    </Tag.CheckableTag>
                    <Tag.CheckableTag
                        checked={quick.assignedTo === 'none'}
                        onChange={(checked) => setQuick({ assignedTo: checked ? 'none' : undefined })}
                        style={quickTagStyle(quick.assignedTo === 'none')}
                    >
                        Nieprzypisani
                    </Tag.CheckableTag>
                    <Tag.CheckableTag
                        checked={quick.remindersOnly}
                        onChange={(checked) => setQuick({ remindersOnly: checked })}
                        style={quickTagStyle(quick.remindersOnly)}
                    >
                        <BellOutlined /> Przypomnienia{statsData?.remindersDue ? ` (${statsData.remindersDue} zaległe)` : ''}
                    </Tag.CheckableTag>
                    <Tag.CheckableTag
                        checked={returningOnly}
                        onChange={(checked) => { setReturningOnly(checked); setPage(1); }}
                        style={quickTagStyle(returningOnly)}
                    >
                        <UserAddOutlined /> Powracający
                    </Tag.CheckableTag>
                    <Tag.CheckableTag
                        checked={readyToContact}
                        onChange={(checked) => { setReadyToContact(checked); setPage(1); }}
                        style={quickTagStyle(readyToContact)}
                    >
                        Gotowe (30+ dni)
                    </Tag.CheckableTag>
                </div>

                {isMobile ? (
                    <List
                        dataSource={contactsData?.data || contactsData || []}
                        loading={contactsLoading}
                        renderItem={(record: any) => (
                            <List.Item style={{ padding: '8px 0', border: 'none' }}>
                                <Card
                                    size="small"
                                    style={{ width: '100%', borderRadius: 8 }}
                                    actions={[
                                        <Button type="text" icon={<PhoneOutlined />} onClick={() => handleOpenContactModal(record)}>Zadzwoń</Button>,
                                        <Button type="text" icon={<HistoryOutlined />} onClick={() => setHistoryContactId(record.id)}>Historia</Button>
                                    ]}
                                >
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                        <div style={{ minWidth: 0 }}>
                                            <div style={{ fontWeight: 600 }}>{record.customerName || 'Brak nazwy'}</div>
                                            <div style={{ fontSize: 12, color: token.colorTextSecondary, wordBreak: 'break-all' }}>{record.customerEmail}</div>
                                            <div style={{ fontSize: 12, marginTop: 4 }}>
                                                <PhoneOutlined /> <a href={`tel:${record.customerPhone}`}>{record.customerPhone || '-'}</a>
                                            </div>
                                        </div>
                                        <Space direction="vertical" size={4} style={{ alignItems: 'flex-end' }}>
                                            <Tag color={contactStatusConfig[record.contactStatus]?.color || 'default'} style={{ margin: 0 }}>
                                                {contactStatusConfig[record.contactStatus]?.label || record.contactStatus}
                                            </Tag>
                                            <ReminderTag date={record.nextContactDate} />
                                        </Space>
                                    </div>
                                    <div style={{ marginTop: 4 }}>
                                        <AssigneeTag user={record.assignedTo} currentUserId={currentUserId} />
                                    </div>
                                    <CustomerBadges history={record.customerHistory} />
                                    <div style={{ marginTop: 12, padding: '8px', background: token.colorFillQuaternary, borderRadius: 4 }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11 }}>
                                            <span>Wartość: <strong>{Number(record.orderTotal || 0).toFixed(2)} zł</strong></span>
                                            <span>Dni: <strong>{dayjs().diff(dayjs(record.deliveredAt || record.createdAt), 'day')}</strong></span>
                                        </div>
                                        <div style={{ marginTop: 6, fontSize: 12 }}>
                                            {productsSummary(record.order?.lines).map((p, i) => (
                                                <div key={i} style={{ color: token.colorText }}>• {p}</div>
                                            ))}
                                        </div>
                                    </div>
                                </Card>
                            </List.Item>
                        )}
                        pagination={{
                            current: page,
                            pageSize: pageSize,
                            total: contactsData?.total || (contactsData?.length || 0),
                            onChange: (p, ps) => { setPage(p); if (ps !== pageSize) setPageSize(ps); },
                            size: 'small',
                            simple: true,
                            style: { textAlign: 'center', marginTop: 16 }
                        }}
                    />
                ) : (
                    <Table
                        columns={columns}
                        dataSource={contactsData?.data || contactsData || []}
                        rowKey="id"
                        loading={contactsLoading}
                        rowSelection={{
                            selectedRowKeys,
                            onChange: (keys) => setSelectedRowKeys(keys),
                        }}
                        pagination={{
                            current: page,
                            pageSize: pageSize,
                            total: contactsData?.total || (contactsData?.length || 0),
                            showSizeChanger: true,
                            showTotal: (total, range) => `${range[0]}-${range[1]} z ${total}`,
                            pageSizeOptions: ['10', '20', '50', '100'],
                        }}
                        onChange={(pagination, _filters, sorter: any) => {
                            // Handle pagination
                            const newPage = pagination.current || 1;
                            const newPageSize = pagination.pageSize || 20;

                            if (newPage !== page) {
                                setPage(newPage);
                            }
                            if (newPageSize !== pageSize) {
                                setPageSize(newPageSize);
                                setPage(1);
                            }

                            // Handle sorting
                            if (sorter.field) {
                                setSortField(sorter.field as string);
                                setSortOrder(sorter.order === 'ascend' ? 'ASC' : 'DESC');
                            } else if (!sorter.order) {
                                // Reset to default when sort is cleared
                                setSortField('deliveredAt');
                                setSortOrder('DESC');
                            }
                        }}
                        scroll={{ x: 'max-content' }}
                        size="middle"
                    />
                )}

            </Card>

            <Modal
                title={`Kontakt: ${selectedContact?.customerName || 'Klient'}`}
                open={isContactModalOpen}
                onCancel={closeContactModal}
                footer={null}
                width={500}
            >
                {selectedContact && (
                    <div style={{ marginBottom: 16 }}>
                        <p><strong>Telefon:</strong> <a href={`tel:${selectedContact.customerPhone}`}>{selectedContact.customerPhone || 'Brak'}</a></p>
                        <p><strong>Email:</strong> {selectedContact.customerEmail || 'Brak'}</p>
                        <p><strong>Zamówienie:</strong> {selectedContact.orderTotal ? `${Number(selectedContact.orderTotal).toFixed(2)} zł` : '-'}</p>
                        {selectedContact.order?.lines?.length > 0 && (
                            <div style={{ marginBottom: 12 }}>
                                <strong>Produkty:</strong>
                                {productsSummary(selectedContact.order.lines).map((p, i) => <div key={i} style={{ fontSize: 13 }}>• {p}</div>)}
                            </div>
                        )}
                        <p><strong>Data dostawy:</strong> {selectedContact.deliveredAt ? dayjs(selectedContact.deliveredAt).format('DD.MM.YYYY') : '-'}</p>
                        <p><strong>Próby kontaktu:</strong> {selectedContact.contactAttempts || 0}</p>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
                            <strong>Opiekun klienta:</strong>
                            <Select
                                size="small"
                                style={{ minWidth: 180, flex: 1 }}
                                placeholder="Nieprzypisany"
                                allowClear
                                showSearch
                                optionFilterProp="label"
                                value={selectedContact.assignedToId || undefined}
                                loading={assignMutation.isPending}
                                onChange={(value) => assignMutation.mutate({ id: selectedContact.id, userId: value || null })}
                                options={assignees.map((u: any) => ({ value: u.id, label: userName(u) }))}
                            />
                            {selectedContact.assignedToId !== currentUserId && currentUserId && (
                                <Button size="small" onClick={() => assignMutation.mutate({ id: selectedContact.id, userId: currentUserId })}>
                                    Przypisz do mnie
                                </Button>
                            )}
                        </div>
                        {selectedContact.assignedTo && selectedContact.assignedToId !== currentUserId && (
                            <Alert
                                type="info"
                                showIcon
                                style={{ marginBottom: 8 }}
                                message={`Klientem zajmuje się ${userName(selectedContact.assignedTo)}`}
                            />
                        )}
                        {selectedContact.customerHistory?.recentlyContacted && (
                            <Alert
                                type="warning"
                                showIcon
                                style={{ marginBottom: 8 }}
                                message="Klient był już niedawno kontaktowany"
                                description={lastTalkText(selectedContact.customerHistory.lastTalk)}
                            />
                        )}
                        {selectedContact.nextContactDate && (
                            <p><strong>Przypomnienie:</strong> <ReminderTag date={selectedContact.nextContactDate} /></p>
                        )}
                        {customerHint(selectedContact.customerHistory) && (
                            <div style={{ fontSize: 12, marginBottom: 8, fontStyle: 'italic' }}>
                                💡 {customerHint(selectedContact.customerHistory)}
                            </div>
                        )}
                        {(selectedContact.customerHistory?.previousOrdersCount > 0
                            || selectedContact.customerHistory?.laterOrdersCount > 0
                            || selectedContact.customerHistory?.previousContactsCount > 0) && (
                            <div style={{ border: `1px solid ${token.colorBorderSecondary}`, padding: 8, borderRadius: 4, marginBottom: 8 }}>
                                <strong>Historia klienta</strong>
                                <CustomerBadges history={selectedContact.customerHistory} />
                                <div style={{ maxHeight: 260, overflow: 'auto', marginTop: 8 }}>
                                    <CustomerTimeline contactId={selectedContact.id} />
                                </div>
                            </div>
                        )}
                        {selectedContact.notes && (
                            <div style={{ background: token.colorFillQuaternary, padding: 8, borderRadius: 4, marginTop: 8 }}>
                                <strong>Poprzednie notatki:</strong>
                                <div style={{ whiteSpace: 'pre-wrap', margin: 0, fontSize: 12, color: token.colorTextSecondary }}><MentionDisplay content={selectedContact.notes} /></div>
                            </div>
                        )}
                    </div>
                )}

                <Form form={contactForm} layout="vertical" onFinish={handleContactSubmit}>
                    <Form.Item
                        name="status"
                        label="Wynik rozmowy"
                        rules={[{ required: true, message: 'Wybierz wynik' }]}
                    >
                        <Select
                            options={[
                                { value: 'CONTACTED', label: '✅ Odebrał - rozmowa' },
                                { value: 'NO_ANSWER', label: '📵 Nie odebrał' },
                                { value: 'REVIEW_REQUESTED', label: '⭐ Poproszono o opinię' },
                                { value: 'CALLBACK', label: '🔄 Oddzwonić później' },
                                { value: 'DECLINED', label: '❌ Odmówił' },
                            ]}
                        />
                    </Form.Item>

                    <Form.Item name="qualityRating" label="Ocena jakości (z rozmowy)">
                        <Rate />
                    </Form.Item>

                    <Form.Item name="notes" label="Notatki z rozmowy">
                        <RichMentionEditor
                            value={contactForm.getFieldValue('notes') || ''}
                            onChange={(val) => contactForm.setFieldValue('notes', val)}
                            placeholder="Co powiedział klient... Wpisz @ aby oznaczyć osobę"
                            minRows={3}
                        />
                    </Form.Item>

                    <Form.Item
                        name="nextContactDate"
                        label="Przypomnienie (oddzwonić)"
                        extra={!selectedContact?.assignedToId ? 'Ustawienie przypomnienia przypisze klienta do Ciebie' : undefined}
                    >
                        <DatePicker
                            showTime
                            format="DD.MM.YYYY HH:mm"
                            placeholder="Wybierz datę"
                            style={{ width: '100%' }}
                        />
                    </Form.Item>

                    {selectedContact?.customerHistory?.openElsewhereCount > 0 && (
                        <Form.Item name="applyToOpenSiblings" valuePropName="checked">
                            <Checkbox>
                                Zapisz wynik także dla {selectedContact.customerHistory.openElsewhereCount === 1
                                    ? 'drugiego otwartego zamówienia'
                                    : `${selectedContact.customerHistory.openElsewhereCount} pozostałych otwartych zamówień`} tego klienta
                                <div style={{ fontSize: 12, color: token.colorTextSecondary }}>
                                    Jedna rozmowa – nikt nie zadzwoni ponownie w sprawie innego zamówienia
                                </div>
                            </Checkbox>
                        </Form.Item>
                    )}

                    <Form.Item>
                        <Space>
                            <Button type="primary" htmlType="submit" loading={updateStatusMutation.isPending}>
                                Zapisz
                            </Button>
                            <Button onClick={closeContactModal}>
                                Anuluj
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>

            <Modal
                title="Historia klienta"
                open={!!historyContactId}
                onCancel={() => setHistoryContactId(null)}
                footer={null}
                width={isMobile ? '100%' : 640}
                destroyOnClose
            >
                {historyContactId && <CustomerTimeline contactId={historyContactId} />}
            </Modal>
        </div >
    );
}
